import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ImExit } from "react-icons/im";
import { MdError } from "react-icons/md";
import { TbWifiOff } from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";
import { io, Socket } from "socket.io-client";

import { Box, Typography } from "@mui/material";
import { red } from "@mui/material/colors";

import { Fade, StyledButton } from "../components";
import { ClientSocket, RoomStatus } from "../config";
import { TestStoreContext } from "../contexts/testStoreContext";
import useQuizSlideInfo from "../hooks/useQuizSlideInfo";
import useWebsocket from "../hooks/useWebsocket";
import { QuizSlideDataSequence } from "../interfaces/question.interface";
import { slideDataIsDiscussion, slideDataIsQuestion } from "../util/questionUtils";
import { generateOrGetClientId } from "../util/webStorage";
import { SlideDataContext } from "./slideDataContext";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const SOCKET_URL =
  /*"wss://natklok-backend-q8rx8.ondigitalocean.app/";*/
  process.env.NODE_ENV === "production"
    ? "wss://natklok-backend-q8rx8.ondigitalocean.app/"
    : "ws://localhost:9001";
const clientId: string = generateOrGetClientId();
const DEFAULT_PLAYER: IPlayer = { name: "", id: clientId };
const DEFAULT_SOCKET: ClientSocket = io(SOCKET_URL, {
  query: { clientId: clientId },
});

export const WebsocketContext = createContext<{
  isMultiplayerHost: boolean;
  socket: ClientSocket;
  myPlayer: IPlayer;
  players: Array<IPlayer>;
  inMultiplayerSession: boolean;
  roomCode: string | undefined;
  leaderboard: Leaderboard;
  connectionError: string;
  disconnectError: string;
  showCountDownScreen: boolean;
  countDownScreenTime: number;
  allowChat: boolean;
  handleSetAllowChat: (val: boolean) => void;
}>({
  isMultiplayerHost: false,
  socket: DEFAULT_SOCKET,
  myPlayer: DEFAULT_PLAYER,
  players: [],
  inMultiplayerSession: false,
  roomCode: undefined,
  leaderboard: [],
  connectionError: "",
  disconnectError: "",
  showCountDownScreen: false,
  countDownScreenTime: 1000,
  allowChat: false,
  handleSetAllowChat: () => null,
});

export interface IPlayer {
  name: string;
  id: string;
}

interface ISocketProvider {
  children: React.ReactChild;
}

type Leaderboard = Array<{ score: number; playerIds: Array<string> }>;

export const SocketProvider = (props: ISocketProvider) => {
  const [isMultiplayerHost, setIsMultiplayerHost] = useState<boolean>(false);
  const [roomCode, setRoomCode] = useState<string | undefined>(undefined);
  const [socket, setSocket] = useState<ClientSocket>(DEFAULT_SOCKET);
  const [myPlayer, setMyPlayer] = useState<IPlayer>(DEFAULT_PLAYER);
  const [players, setPlayers] = useState<Array<IPlayer>>([]);
  const [allowChat, setAllowChat] = useState<boolean>(false);
  const [leaderboard, setLeaderboard] = useState<Leaderboard>([]);
  const [connectionError, setConnectionError] = useState<string>("");
  const [disconnectError, setDisconnectError] = useState<string>("");
  const [inMultiplayerSession, setInMultiplayerSession] = useState<boolean>(false);
  const [reconnectPopupVisible, setReconnectPopupVisible] = useState<boolean>(false);
  const [showCountDownScreen, setShowCountDownScreen] = useState<boolean>(false);
  const [countDownScreenTime] = useState<number>(
    process.env.NODE_ENV === "production" ? 5000 : 1000
  );
  const { questionsReducer } = useContext(SlideDataContext);
  const { setBadgeIdOfCurrentTest } = useContext(TestStoreContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { currentSlideIndex } = useQuizSlideInfo();
  const { quizSlideDataSequence } = useContext(SlideDataContext);

  const handleSetAllowChat = (val: boolean) => {
    setAllowChat(val);
    socket.emit("setChatAllowed", val);
  };

  const handleReconnect = () => {
    if (socket.connected === false) {
      resetSocket();
    }
    setReconnectPopupVisible(false);
  };

  const resetRoomState = useCallback(() => {
    setIsMultiplayerHost(false);
    setPlayers([]);
    setMyPlayer(DEFAULT_PLAYER);
    setRoomCode(undefined);
    setInMultiplayerSession(false);
    navigate("/", { replace: true });
    setAllowChat(false);
  }, [navigate]);

  const resetSocket = useCallback(() => {
    // Close current socket and create a new one
    socket.close();
    setSocket(
      io(SOCKET_URL, {
        query: { clientId: clientId },
      })
    );
  }, [socket]);

  const prettyLog = (eventName: string, args: any, background = "purple") => {
    console.log(
      "%c" + eventName,
      "font-weight: bold; font-size; 18px; background: " + background + "; color: white",
      "\n ",
      args
    );
  };

  const setQuizSlideSequenceFromServer = (quizSlideDataSequence: QuizSlideDataSequence) => {
    questionsReducer({ type: "setQuizSlideDataSequence", quizSlideDataSequence });
  };

  const startQuiz = useCallback(
    (quizSlideDataSequence: QuizSlideDataSequence, currentQuestionIndex: number) => {
      setQuizSlideSequenceFromServer(quizSlideDataSequence);
      questionsReducer({ type: "completeQuestionsToIndex", index: currentQuestionIndex });
      setBadgeIdOfCurrentTest(undefined);
      navigate("/questions", { replace: true });
    },
    [navigate, questionsReducer, setBadgeIdOfCurrentTest]
  );

  const showResultsPage = useCallback(() => {
    navigate("/multiplayer/resultat");
  }, [navigate]);

  const showWaitingRoom = useCallback(() => {
    navigate("/multiplayer/vanterum");
  }, [navigate]);

  useEffect(() => {
    const handler = (eventName: string, ...args: any[]) => {
      //prettyLog(eventName, args);
    };
    socket.onAny(handler);
    return () => {
      socket.offAny(handler);
    };
  }, [socket]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      socket.emit("frontendHeartbeat");
    }, 1000 * 60 * 5);

    return () => clearInterval(intervalId);
  }, [socket]);

  useEffect(() => {
    socket.on("connect", () => {
      prettyLog("connect", "");
      setDisconnectError("");
      setConnectionError("");
    });
    return () => {
      socket.off("connect");
    };
  }, [socket]);

  useEffect(() => {
    socket.on("roomHosted", () => {
      setIsMultiplayerHost(true);
    });
    return () => {
      socket.off("roomHosted");
    };
  }, [socket]);

  useEffect(() => {
    socket.on("chatAllowed", (val: boolean) => {
      setAllowChat(val);
    });
    return () => {
      socket.off("chatAllowed");
    };
  }, [socket]);

  useEffect(() => {
    socket.on(
      "sendBufferedGameData",
      (
        state: RoomStatus,
        quizSlideDataSequence: QuizSlideDataSequence,
        currentQuestionIndex: number,
        showReconnectModal: boolean,
        chatAllowed: boolean
      ) => {
        setQuizSlideSequenceFromServer(quizSlideDataSequence);
        setAllowChat(chatAllowed);

        if (state === "quiz") {
          startQuiz(quizSlideDataSequence, currentQuestionIndex);
        } else if (state === "results") {
          showResultsPage();
        } else if (state === "waiting") {
          showWaitingRoom();
        }

        if (state !== "waiting" && showReconnectModal === true) {
          setReconnectPopupVisible(true);
        }
      }
    );

    return () => {
      socket.off("sendBufferedGameData");
    };
  }, [isMultiplayerHost, questionsReducer, startQuiz, showResultsPage, showWaitingRoom, socket]);

  useEffect(() => {
    socket.on("roomJoined", (players: Array<IPlayer>, code: string) => {
      setPlayers(players);

      setRoomCode(code);
      setInMultiplayerSession(true);
    });
    return () => {
      socket.off("roomJoined");
    };
  }, [socket]);

  useEffect(() => {
    socket.on("roomLeft", (player: IPlayer) => {
      if (player.id === myPlayer.id) {
        resetRoomState();
      } else {
        const newPlayers: Array<IPlayer> = [...players];
        const changedPlayerIndex = newPlayers.findIndex((p: any) => p.id === player.id);
        if (changedPlayerIndex !== -1) {
          newPlayers.splice(changedPlayerIndex, 1);
        }

        setPlayers(newPlayers);
      }
    });
    return () => {
      socket.off("roomLeft");
    };
  }, [myPlayer.id, players, resetRoomState, socket]);

  useEffect(() => {
    socket.on("clientDataUpdated", (updatedPlayer: IPlayer) => {
      if (updatedPlayer.id === myPlayer.id) {
        setMyPlayer(updatedPlayer);
      }
      const newPlayers: Array<IPlayer> = [...players];
      const newPlayer: IPlayer = updatedPlayer;
      const changedPlayerIndex = newPlayers.findIndex((player: any) => player.id === newPlayer.id);
      if (changedPlayerIndex !== -1) {
        newPlayers.splice(changedPlayerIndex, 1, newPlayer);
      }
      setPlayers(newPlayers);
    });
    return () => {
      socket.off("clientDataUpdated");
    };
  }, [myPlayer, players, socket]);

  useEffect(() => {
    socket.on(
      "updateAnswerStats",
      (quizSlideDataSequence: QuizSlideDataSequence, leaderboard: Leaderboard) => {
        questionsReducer({
          type: "updateAnswerStatsFromServerQuizSlideDataSequence",
          quizSlideDataSequence,
        });
        setLeaderboard(leaderboard);
      }
    );
    return () => {
      socket.off("updateAnswerStats");
    };
  }, [socket]);

  useEffect(() => {
    socket.on("quizOver", () => {
      showResultsPage();
    });
    return () => {
      socket.off("quizOver");
    };
  }, [navigate, showResultsPage, socket]);

  useEffect(() => {
    socket.on("gameEnded", () => {
      resetRoomState();
    });
    return () => {
      socket.off("gameEnded");
    };
  }, [resetRoomState, socket]);

  useEffect(() => {
    socket.on("serverMessage", (text: string) => {
      if (inMultiplayerSession || location.pathname === "/multiplayer/joina") {
        alert("Spelnotis: " + text);
      }
    });
    return () => {
      socket.off("serverMessage");
    };
  }, [inMultiplayerSession, location.pathname, socket]);

  useEffect(() => {
    socket.on("startQuiz", (quiz: QuizSlideDataSequence) => {
      startQuiz(quiz, 0);
    });

    return () => {
      socket.off("startQuiz");
    };
  }, [navigate, questionsReducer, setBadgeIdOfCurrentTest, startQuiz, socket]);

  useEffect(() => {
    socket.on("nextQuestion", () => {
      const nextSlideData = quizSlideDataSequence[currentSlideIndex + 1];
      if (slideDataIsQuestion(nextSlideData) || slideDataIsDiscussion(nextSlideData)) {
        setShowCountDownScreen(true);
        setTimeout(() => {
          setShowCountDownScreen(false);
        }, countDownScreenTime);
      }

      questionsReducer({
        type: "completeCurrentQuestion",
      });
    });
    return () => {
      socket.off("nextQuestion");
    };
  }, [
    countDownScreenTime,
    currentSlideIndex,
    location.pathname,
    navigate,
    questionsReducer,
    quizSlideDataSequence,
    socket,
  ]);

  useEffect(() => {
    socket.on("gameRestarted", () => {
      if (isMultiplayerHost) {
        navigate("/multiplayer/skapa", { replace: true });
      } else {
        showWaitingRoom();
      }
    });
    return () => {
      socket.off("gameRestarted");
    };
  }, [isMultiplayerHost, navigate, showWaitingRoom, socket]);

  useEffect(() => {
    socket.on("connect_error", (err) => {
      prettyLog("connect_error", err.message, "red");

      if (err.message === "xhr poll error") {
        setConnectionError("Det går inte att nå servern");
      } else {
        setConnectionError(err.message);
      }
    });
    return () => {
      socket.off("connect_error");
    };
  }, [resetRoomState, socket]);

  useEffect(() => {
    socket.on("disconnect", (reason: Socket.DisconnectReason, description: any) => {
      prettyLog("disconnect", reason + ", " + description?.description, "red");

      setDisconnectError(reason);
    });
    return () => {
      socket.off("disconnect");
    };
  }, [inMultiplayerSession, resetRoomState, resetSocket, socket]);

  useEffect(() => {
    if (socket.connected === false && inMultiplayerSession === true) {
      setReconnectPopupVisible(true);
    }
  }, [inMultiplayerSession, socket.connected]);

  return (
    <WebsocketContext.Provider
      value={{
        inMultiplayerSession,
        isMultiplayerHost,
        socket,
        myPlayer,
        players,
        roomCode,
        leaderboard,
        connectionError,
        disconnectError,
        showCountDownScreen,
        countDownScreenTime,
        allowChat,
        handleSetAllowChat,
      }}
    >
      {/*For testing*/}
      {/*process.env.NODE_ENV === "development" && (
        <button
          onClick={() => {
            socket.close();
            alert("Killed connection");
          }}
        >
          Kill connection
        </button>
        )*/}

      <ErrorMessage
        disconnectError={disconnectError}
        connectionError={connectionError}
        inMultiplayerSession={inMultiplayerSession}
        resetRoomState={resetRoomState}
      />
      <ReconnectPopup
        reconnect={handleReconnect}
        visible={reconnectPopupVisible}
        inMultiplayerSession={inMultiplayerSession}
        resetRoomState={resetRoomState}
      />
      {props.children}
    </WebsocketContext.Provider>
  );
};

const ReconnectPopup: React.FC<{
  visible: boolean;
  reconnect: () => void;
  inMultiplayerSession: boolean;
  resetRoomState: () => void;
}> = ({ visible, reconnect, inMultiplayerSession, resetRoomState }) => {
  const { wsLeaveRoom } = useWebsocket();
  const translatePageText = useTranslatePageText();

  if (inMultiplayerSession === true && visible === true) {
    return (
      <div
        style={{
          position: "fixed",
          zIndex: 101,
          width: "100%",
          height: "100%",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            bgcolor={"white"}
            padding={2}
            margin={2}
            borderRadius={1}
            maxWidth={400}
            maxHeight={200}
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{ color: "black" }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography fontWeight="bold" variant="h6">
                {translatePageText("forTeachers.brokenConnection")}
              </Typography>
              <TbWifiOff style={{ marginLeft: 8, fontSize: 24 }} />
            </div>

            <Typography
              variant="body2"
              lineHeight={1.2}
              style={{ color: "black", marginBottom: 24, marginTop: 8 }}
            >
              {translatePageText("forTeachers.reconnectQuestion")}
            </Typography>
            <StyledButton fullWidth size="small" onClick={reconnect} style={{ marginBottom: 4 }}>
              {translatePageText("forTeachers.reconnect")}
            </StyledButton>
            <StyledButton
              variant="tertiary"
              size="small"
              icon={<ImExit />}
              onClick={() => {
                const leave = window.confirm("Är du säker på att du vill lämna spelet?");
                if (leave) {
                  wsLeaveRoom();
                  resetRoomState();
                }
              }}
              fullWidth
              style={{
                background: "transparent",
                boxShadow: undefined,
              }}
            >
              {translatePageText("forTeachers.leave")}
            </StyledButton>
          </Box>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const ErrorMessage: React.FC<{
  disconnectError: string;
  connectionError: string;
  inMultiplayerSession: boolean;
  resetRoomState: () => void;
}> = ({ disconnectError, connectionError, inMultiplayerSession, resetRoomState }) => {
  const location = useLocation();
  const [notConnectedError, setNotConnectedError] = useState("");

  useEffect(() => {
    if (
      inMultiplayerSession === false &&
      location.pathname !== "/multiplayer/skapa" &&
      location.pathname !== "/multiplayer/joina" &&
      location.pathname !== "/multiplayer/info"
    ) {
      setNotConnectedError("kunde inte anropa servern");
    } else {
      setNotConnectedError("");
    }
  }, [inMultiplayerSession, location]);

  if (inMultiplayerSession === false) return null;
  if (disconnectError === "" && connectionError === "" && notConnectedError === "") return null;

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 100,
        width: "100%",
        height: "100%",
      }}
    >
      <Fade delay={100}>
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            bgcolor={red[50]}
            padding={2}
            borderLeft={"4px solid " + red[500]}
            borderRadius={1}
            maxWidth={400}
            style={{ color: red[600] }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography fontWeight="bold">
                Något gick fel, en sekund... Försöker återansluta...
              </Typography>
              <MdError style={{ marginLeft: 8, fontSize: 28 }} />
            </div>

            <Typography lineHeight={1.2} margin="2px 0">
              Anledning: {connectionError}, {disconnectError}, {notConnectedError}.
            </Typography>

            <Typography
              variant="body2"
              lineHeight={1.2}
              style={{ color: red[200], margin: "4px 0" }}
            >
              Anmäl gärna detta till oss via info@stoldskyddsforeningen.se <br />
            </Typography>
            <StyledButton
              onClick={resetRoomState}
              size="small"
              style={{ marginTop: 12, background: red[400] }}
            >
              Lämna spelet
            </StyledButton>
          </Box>
        </div>
      </Fade>
    </div>
  );
};
